import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Axios instance for locations API calls
 * - Base URL: `${API_BASE_URL}/api/v1`
 * - JSON content type by default
 */
const locationsAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

/**
 * Request interceptor
 * - Attaches available auth token to outgoing requests
 */
locationsAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return applyWarehouseFilter(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * - Handles 401 Unauthorized responses by clearing auth storage and redirecting to login
 */
locationsAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);


/**
 * LocationsService - wrapper for location-related API endpoints
 * - Provides CRUD operations, bulk operations and state/city fetching
 */
class LocationsService {

  /**
   * Fetch locations with optional pagination and filters
   * @param {Object} params - Query parameters
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getLocations(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      const limit = params.limit ?? 20;
      const page = params.page ?? null;
      const skip = params.skip !== undefined
        ? params.skip
        : page !== null
          ? (page - 1) * limit
          : 0;

      queryParams.append('skip', skip);
      queryParams.append('limit', limit);

      if (params.search !== undefined) queryParams.append('search', params.search);
      if (params.state) queryParams.append('state', params.state);
      if (params.city) queryParams.append('city', params.city);
      if (params.location_type) queryParams.append('location_type', params.location_type);
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);

      const response = await locationsAPI.get(`/locations?${queryParams.toString()}`);

      const data = response.data;
      return {
        success: true,
        data: data?.locations || data?.items || data || [],
        pagination: data?.pagination || {},
        total: data?.total || data?.count || (Array.isArray(data) ? data.length : 0),
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Get a single location by ID
   * @param {string|number} locationId - Location ID
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getLocationById(locationId) {
    try {
      const response = await locationsAPI.get(`/locations/${locationId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Create a new location
   * @param {Object} locationData - Location payload
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async createLocation(locationData) {
    try {
      const response = await locationsAPI.post('/locations', locationData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Update an existing location
   * @param {string|number} locationId - Location ID
   * @param {Object} locationData - Updated location data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async updateLocation(locationId, locationData) {
    try {
      const response = await locationsAPI.put(`/locations/${locationId}`, locationData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Delete a location
   * @param {string|number} locationId - Location ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteLocation(locationId) {
    try {
      await locationsAPI.delete(`/locations/${locationId}`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Bulk delete multiple locations
   * @param {Array<string|number>} locationIds - Array of location IDs
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async bulkDeleteLocations(locationIds) {
    try {
      // Send the array directly as the request body instead of wrapping in an object
      const response = await locationsAPI.post('/locations/bulk-delete', locationIds);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Fetch all states
   * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
   */
  async getStates() {
    try {
      const response = await locationsAPI.get('/locations/states');
      return {
        success: true,
        data: response.data.states || response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Fetch cities for a specific state
   * @param {string} stateId - State ID or name
   * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
   */
  async getCitiesByState(stateId) {
    try {
      const response = await locationsAPI.get(`/locations/states/${stateId}/cities`);
      return {
        success: true,
        data: response.data.cities || response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Export locations to CSV
   * @param {Object} filters - Export filters
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async exportLocations(filters = {}) {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append('format', 'csv');

      if (filters.state) queryParams.append('state', filters.state);
      if (filters.city) queryParams.append('city', filters.city);
      if (filters.location_type) queryParams.append('location_type', filters.location_type);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);

      const response = await locationsAPI.get(`/locations/export?${queryParams.toString()}`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }
}

// Export singleton instance
const locationsService = new LocationsService();
export default locationsService;